import { useEffect, useMemo, type FC } from 'react';
import { useConsentGated, useOneTrustCountry, type ConsentCategory } from '@fff-web/consent';
import { removeScript } from '../../core/dom';
import { AfterShipAdsContext } from './AfterShipAds.context';
import type { AfterShipAdsProviderProps } from './AfterShipAds.types';

/** OneTrust group covering targeting / advertising cookies. */
const TARGETING_CONSENT_CATEGORY: ConsentCategory = 'C0004';

/**
 * The countries the AfterShip placement is contracted for. Add to this list to open the
 * ads up to another market -- nothing else in the gate needs to change.
 *
 * Kept local rather than pulled from `@fff-web/constants`: the country list there
 * (`ALLOWED_COUNTRIES`) scopes the OneTrust opt-out UI, which is a different question
 * that happens to share a value today.
 */
const ALLOWED_COUNTRIES: readonly string[] = ['US'];

export const AfterShipAdsProvider: FC<AfterShipAdsProviderProps> = ({
  children,
  appKey,
  environment,
  isEnabled = false,
  logger,
  onImpression,
  onAdClick,
}) => {
  // OneTrust resolves asynchronously and mutates when the member changes preferences, so
  // consent is state rather than a one-shot read. `isLoading` keeps an unresolved banner
  // distinct from a denial: only an actual denial should tear an already-loaded SDK out.
  const { isGranted, isLoading } = useConsentGated(TARGETING_CONSENT_CATEGORY);
  const { country, isLoading: isCountryLoading } = useOneTrustCountry();

  // The gate fails closed: `country` is null both while OneTrust is still resolving and
  // when it never answers at all, and neither is a positive confirmation the member is
  // in a country we serve. Requiring a match outright also means no slot is ever
  // rendered and then torn back out once geolocation lands -- an impression that has
  // already been served cannot be unserved.
  const isPermitted = isGranted && country !== null && ALLOWED_COUNTRIES.includes(country);

  // Losing permission mid-session must take the tag back out of the document, not just
  // stop the next request. Keyed off the whole gate, not consent alone -- a geolocation
  // that resolves non-US after the script loaded has to tear it out too.
  const isResolving = isLoading || isCountryLoading;

  useEffect(() => {
    if (isResolving || isPermitted) return;
    removeScript(logger ?? console);
  }, [isPermitted, isResolving, logger]);

  const contextValue = useMemo(
    () => ({
      appKey,
      environment,
      logger,
      isAllowed: Boolean(isEnabled && appKey && isPermitted),
      onImpression,
      onAdClick,
    }),
    [appKey, environment, logger, isEnabled, isPermitted, onImpression, onAdClick],
  );

  return (
    <AfterShipAdsContext.Provider value={contextValue}>{children}</AfterShipAdsContext.Provider>
  );
};
